//@flow
import React from 'react';
import {makeStyles, Typography, Divider} from '@material-ui/core';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {GetApp, CallMade, Publish} from '@material-ui/icons';
import {colors} from '../../../assets/styles/colors';
import {
  useProductBatchLoadTemplates,
  useProductBatchLoadActions,
  useProductBatchLoadUpdateState,
} from '../utils';
import BatchUploadResult from './BatchUploadResult';

import InlineLoader from '../../../elements/LoadingSpinner/InlineLoader';
import {useAlert} from '../../../elements/Alert/AlertContext';
import {useIsFeatureInTier} from '../../../utils/component';
import {useTierRestrictionActions} from '../../TierRestriction/utils';
import CustomAlert from '../../../elements/CustomAlert/CustomAlert';
import {TIENDADA_LINKS} from '../../../constants/tiendada-links';

const useStyles = makeStyles(theme => ({
  root: {
    padding: '1rem',
    background: 'white',
    boxShadow: '0px 0px 1px rgba(40, 41, 61, 0.08)',
    borderRadius: '.5rem',
  },
  sectionContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    gap: '1rem',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  section: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'flex-start',
    gap: '.5rem',
    position: 'relative',
  },
  sectionInformation: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  subtitle: {
    fontSize: '.875rem',
    fontWeight: 600,
  },
  description: {
    fontSize: '.875rem',
    color: colors.grey700,
  },
  divider: {
    margin: '1rem 0',
  },
  uploadArea: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    aspectRatio: '3/2',
    background: colors.grey100,
    borderRadius: '.5rem',
    border: `1px solid ${colors.grey300}`,
    maxHeight: '200px',
    gap: '.5rem',
    flexDirection: 'column',
    color: colors.primary,
    cursor: 'pointer',
  },
  icon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  caption: {
    fontSize: '.75rem',
    color: colors.grey700,
    textAlign: 'center',
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    background: 'rgba(255, 255, 255, .8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default function ProductBatchLoadUpdate() {
  const classes = useStyles();

  const {editTemplateLink} = useProductBatchLoadTemplates();
  const {
    batchLoadTask,
    loading: gettingTask,
  } = useProductBatchLoadUpdateState();
  const {showAlert} = useAlert();

  const {update, loading: uploading} = useProductBatchLoadActions();

  const hasMassiveProductUploadFeature = useIsFeatureInTier(
    'MASSIVE_PRODUCT_UPLOAD'
  );

  const {showTierRestrictionModal} = useTierRestrictionActions();

  const handleUpload = e => {
    const file = e.target.files[0];

    if (!file) return;

    update(file).catch(error => {
      console.log({error});
      showAlert(error.message, {type: 'error'});
    });
    e.target.value = null;
  };

  const handleLabelClick = () => {
    if (hasMassiveProductUploadFeature) return;
    showTierRestrictionModal();
  };

  const loading = gettingTask || uploading;

  return (
    <aside className={classes.root}>
      <section className={classes.sectionContainer}>
        <div className={classes.sectionInformation}>
          <Typography className={classes.subtitle}>
            1. Descargar Excel
          </Typography>
          <Typography className={classes.description}>
            Descarga plantilla de productos
          </Typography>
        </div>
        <div>
          <CustomButton
            startIcon={<GetApp />}
            color="primary"
            variant="text"
            href={editTemplateLink}
            target="_blank"
            disabled={!hasMassiveProductUploadFeature}
            rel="noopener noreferrer"
          >
            Descargar Excel
          </CustomButton>
        </div>
      </section>

      <Divider className={classes.divider} />

      <section className={classes.sectionContainer}>
        <div className={classes.sectionInformation}>
          <Typography className={classes.subtitle}>
            2. Completar la información
          </Typography>
          <Typography className={classes.description}>
            Productos siguiendo las instrucciones.
          </Typography>
        </div>
        <div>
          <CustomButton
            endIcon={<CallMade />}
            color="primary"
            variant="text"
            href={TIENDADA_LINKS.tutorials.BULK_PRODUCT_UPLOAD_AND_EDIT}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ver Instrucciones
          </CustomButton>
        </div>
      </section>

      <Divider className={classes.divider} />

      <section className={classes.section}>
        {loading && (
          <div className={classes.loader}>
            <InlineLoader loading={loading} />
          </div>
        )}
        <Typography className={classes.subtitle}>3. Subir el Excel</Typography>
        <Typography className={classes.description}>
          Sube tu plantilla una vez que hayas terminado la edición de tus
          productos
        </Typography>

        <CustomAlert open className={classes.alert} severity="info">
          <strong>Importante:</strong>
          <br />
          • Solo sube la plantilla descargada arriba
          <br />
          • Las variantes deben existir previamente
          <br />• No modifiques los nombres de las columnas ni su orden
        </CustomAlert>
        {batchLoadTask?.state !== 'PENDING' && (
          <label className={classes.uploadArea} onClick={handleLabelClick}>
            <span className={classes.icon}>
              <Publish />
            </span>
            <span className={classes.label}>Subir plantilla</span>
            <input
              type="file"
              hidden
              disabled={uploading || !hasMassiveProductUploadFeature}
              accept=".xlsx, .xls"
              onChange={handleUpload}
            />
          </label>
        )}
        {batchLoadTask?.state === 'PENDING' && (
          <div className={classes.uploadArea}>
            <strong className={classes.label}>Procesando...</strong>
            <strong className={classes.caption}>
              Puede tardar entre 1 minuto a 2 horas.
            </strong>
            <span className={classes.caption}>
              Puedes salir de esta página sin problemas, mientras tu documento
              sigue procesando
            </span>
          </div>
        )}
      </section>

      {!!batchLoadTask && batchLoadTask?.state !== 'PENDING' && (
        <>
          <Divider className={classes.divider} />

          <Typography className={classes.subtitle}>
            Último Documento Subido
          </Typography>

          <BatchUploadResult batchTask={batchLoadTask} />
        </>
      )}
    </aside>
  );
}
